import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ApiService } from '../../services/api.service';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

interface DatabaseInfo {
  name: string;
  isTracked: boolean;
  hasLogs: boolean;
  hasVersions: boolean;
  hasTrigger: boolean;
  initializing: boolean;
  settingUpVersions: boolean;
  statusLoading: boolean;
  error: string | null;
  success: string | null;
}

@Component({
  selector: 'app-databases-management',
  imports: [CommonModule],
  templateUrl: './databases-management.component.html',
  styleUrl: './databases-management.component.scss'
})
export class DatabasesManagementComponent implements OnInit {
  databases: DatabaseInfo[] = [];
  loading = false;
  error: string | null = null;

  showTrackDbSql = false;
  showSetupVersionsSql = false;

  readonly trackDbSql = `-- ZBerale_DatabaseLogs
IF NOT EXISTS (SELECT 1 FROM sys.objects WHERE name = 'ZBerale_DatabaseLogs' AND type = 'U')
CREATE TABLE [dbo].[ZBerale_DatabaseLogs](
    [Id]               INT           IDENTITY(1,1) NOT NULL,
    [DateTime]         DATETIME      NOT NULL DEFAULT GETDATE(),
    [ServerName]       NVARCHAR(128) NULL,
    [ServiceName]      NVARCHAR(128) NULL,
    [SPID]             INT           NULL,
    [SourceHostName]   NVARCHAR(128) NULL,
    [LoginName]        NVARCHAR(128) NULL,
    [UserName]         NVARCHAR(128) NULL,
    [SchemaName]       NVARCHAR(128) NULL,
    [ObjectName]       NVARCHAR(128) NULL,
    [TargetObjectName] NVARCHAR(128) NULL,
    [EventType]        NVARCHAR(128) NULL,
    [ObjectType]       NVARCHAR(128) NULL,
    [TargetObjectType] NVARCHAR(128) NULL,
    [CommandText]      NVARCHAR(MAX) NULL,
    [EventData]        XML           NULL,
    [sequence]         INT           NULL,
    CONSTRAINT [PK_ZBerale_DatabaseLogs] PRIMARY KEY CLUSTERED ([Id] ASC)
)
GO

-- ZBerale_Versions
IF NOT EXISTS (SELECT 1 FROM sys.objects WHERE name = 'ZBerale_Versions' AND type = 'U')
CREATE TABLE [dbo].[ZBerale_Versions](
    [VersionID]   INT          IDENTITY(1,1) NOT NULL,
    [VersionName] VARCHAR(100) NOT NULL,
    [VersionDate] DATETIME     NOT NULL DEFAULT GETDATE(),
    [DBLogID]     INT          NULL,
    CONSTRAINT [PK_ZBerale_Versions]      PRIMARY KEY CLUSTERED ([VersionID] ASC),
    CONSTRAINT [UQ_ZBerale_Versions_Name] UNIQUE ([VersionName])
)
GO

-- ZBerale_LKUP_Labels
IF NOT EXISTS (SELECT 1 FROM sys.objects WHERE name = 'ZBerale_LKUP_Labels' AND type = 'U')
CREATE TABLE [dbo].[ZBerale_LKUP_Labels](
    [LabelID]     INT          IDENTITY(1,1) NOT NULL,
    [LabelDesc]   VARCHAR(100) NOT NULL,
    [CreatedDate] DATETIME     NOT NULL DEFAULT GETDATE(),
    [IsActive]    BIT          NOT NULL DEFAULT 1,
    CONSTRAINT [PK_ZBerale_LKUP_Labels]      PRIMARY KEY CLUSTERED ([LabelID] ASC),
    CONSTRAINT [UQ_ZBerale_LKUP_Labels_Desc] UNIQUE ([LabelDesc])
)
GO

-- ZBerale_DatabaseLog_Labels
IF NOT EXISTS (SELECT 1 FROM sys.objects WHERE name = 'ZBerale_DatabaseLog_Labels' AND type = 'U')
CREATE TABLE [dbo].[ZBerale_DatabaseLog_Labels](
    [LogLabelID]   INT          IDENTITY(1,1) NOT NULL,
    [LogID]        INT          NOT NULL,
    [LabelID]      INT          NOT NULL,
    [AssignedDate] DATETIME     NOT NULL DEFAULT GETDATE(),
    [AssignedBy]   VARCHAR(255) NULL,
    CONSTRAINT [PK_ZBerale_DatabaseLog_Labels] PRIMARY KEY CLUSTERED ([LogLabelID] ASC),
    CONSTRAINT [FK_DatabaseLog_Labels_Log]   FOREIGN KEY ([LogID])   REFERENCES [dbo].[ZBerale_DatabaseLogs]([Id])    ON DELETE CASCADE,
    CONSTRAINT [FK_DatabaseLog_Labels_Label] FOREIGN KEY ([LabelID]) REFERENCES [dbo].[ZBerale_LKUP_Labels]([LabelID]) ON DELETE CASCADE,
    CONSTRAINT [UQ_DatabaseLog_Labels] UNIQUE ([LogID], [LabelID])
)
GO

-- DDL Trigger
IF EXISTS (SELECT 1 FROM sys.triggers WHERE name = 'trg_DDL_DatabaseLog' AND parent_class = 0)
    DROP TRIGGER [trg_DDL_DatabaseLog] ON DATABASE
GO
CREATE TRIGGER [trg_DDL_DatabaseLog]
ON DATABASE
FOR CREATE_PROCEDURE, ALTER_PROCEDURE, DROP_PROCEDURE,
    CREATE_TABLE,     ALTER_TABLE,     DROP_TABLE,
    CREATE_FUNCTION,  ALTER_FUNCTION,  DROP_FUNCTION,
    CREATE_VIEW,      ALTER_VIEW,      DROP_VIEW,
    CREATE_INDEX,     ALTER_INDEX,     DROP_INDEX,
    CREATE_TRIGGER,   ALTER_TRIGGER,   DROP_TRIGGER
AS
BEGIN
    SET NOCOUNT ON;
    DECLARE @EventData   XML           = EVENTDATA();
    DECLARE @EventType   NVARCHAR(128) = @EventData.value('(/EVENT_INSTANCE/EventType)[1]',               'NVARCHAR(128)');
    DECLARE @ObjectName  NVARCHAR(128) = @EventData.value('(/EVENT_INSTANCE/ObjectName)[1]',              'NVARCHAR(128)');
    DECLARE @ObjectType  NVARCHAR(128) = @EventData.value('(/EVENT_INSTANCE/ObjectType)[1]',              'NVARCHAR(128)');
    DECLARE @SchemaName  NVARCHAR(128) = @EventData.value('(/EVENT_INSTANCE/SchemaName)[1]',              'NVARCHAR(128)');
    DECLARE @CommandText NVARCHAR(MAX) = @EventData.value('(/EVENT_INSTANCE/TSQLCommand/CommandText)[1]', 'NVARCHAR(MAX)');
    DECLARE @LoginName   NVARCHAR(128) = ORIGINAL_LOGIN();
    DECLARE @UserName    NVARCHAR(128) = SYSTEM_USER;
    DECLARE @HostName    NVARCHAR(128) = HOST_NAME();
    DECLARE @ServerName  NVARCHAR(128) = @@SERVERNAME;
    DECLARE @ServiceName NVARCHAR(128) = @@SERVICENAME;
    DECLARE @SPID        INT           = @@SPID;
    INSERT INTO [dbo].[ZBerale_DatabaseLogs]
        ([DateTime],[ServerName],[ServiceName],[SPID],[SourceHostName],[LoginName],[UserName],[SchemaName],[ObjectName],[EventType],[ObjectType],[CommandText],[EventData])
    VALUES
        (GETDATE(),@ServerName,@ServiceName,@SPID,@HostName,@LoginName,@UserName,@SchemaName,@ObjectName,@EventType,@ObjectType,@CommandText,@EventData);
END
GO

-- Initial version entry
IF NOT EXISTS (SELECT 1 FROM ZBerale_Versions WHERE VersionName = 'Unreleased')
    INSERT INTO ZBerale_Versions (VersionName, VersionDate, DBLogID)
    VALUES ('Unreleased', GETDATE(), 1)`;

  readonly setupVersionsSql = `IF NOT EXISTS (SELECT 1 FROM sys.objects WHERE name = 'ZBerale_Versions' AND type = 'U')
BEGIN
    CREATE TABLE [dbo].[ZBerale_Versions](
        [VersionID]   [int]          IDENTITY(1,1) NOT NULL,
        [VersionName] [varchar](100) NOT NULL,
        [VersionDate] [datetime]     NOT NULL DEFAULT GETDATE(),
        CONSTRAINT [PK_ZBerale_Versions]      PRIMARY KEY CLUSTERED ([VersionID] ASC),
        CONSTRAINT [UQ_ZBerale_Versions_Name] UNIQUE ([VersionName])
    )
    INSERT INTO [dbo].[ZBerale_Versions] (VersionName, VersionDate)
    VALUES ('v1.0.0', GETDATE())
END`;

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.loadDatabases();
  }

  loadDatabases(): void {
    this.loading = true;
    this.error = null;

    this.apiService.getDatabases().subscribe({
      next: (names) => {
        this.databases = names.map(name => ({
          name,
          isTracked: false,
          hasLogs: false,
          hasVersions: false,
          hasTrigger: false,
          initializing: false,
          settingUpVersions: false,
          statusLoading: true,
          error: null,
          success: null
        }));
        this.loading = false;
        this.loadStatuses();
      },
      error: (err) => {
        this.error = 'Failed to load databases: ' + (err.error?.error || err.message);
        this.loading = false;
      }
    });
  }

  loadStatuses(): void {
    this.databases.forEach(db => {
      this.apiService.getDatabaseStatus(db.name).subscribe({
        next: (status) => {
          db.isTracked   = status.isTracked;
          db.hasLogs     = status.hasLogs;
          db.hasVersions = status.hasVersions;
          db.hasTrigger  = status.hasTrigger;
          db.statusLoading = false;
        },
        error: () => {
          db.statusLoading = false;
        }
      });
    });
  }

  initialize(db: DatabaseInfo): void {
    if (!confirm(`Initialize Klemantina tracking for "${db.name}"?\n\nThis will create the required tables and DDL trigger.`)) {
      return;
    }

    db.initializing = true;
    db.error = null;
    db.success = null;

    this.apiService.initializeDatabase(db.name).subscribe({
      next: (result) => {
        db.success = result.message;
        db.initializing = false;
        // Refresh status
        db.statusLoading = true;
        this.apiService.getDatabaseStatus(db.name).subscribe({
          next: (status) => {
            db.isTracked   = status.isTracked;
            db.hasLogs     = status.hasLogs;
            db.hasVersions = status.hasVersions;
            db.hasTrigger  = status.hasTrigger;
            db.statusLoading = false;
          },
          error: () => { db.statusLoading = false; }
        });
      },
      error: (err) => {
        db.error = 'Initialization failed: ' + (err.error?.error || err.message);
        db.initializing = false;
      }
    });
  }

  setupVersions(db: DatabaseInfo): void {
    if (!confirm(`Create ZBerale_Versions table in "${db.name}"?\n\nThis will create the table and insert an initial 'v1.0.0' entry.`)) {
      return;
    }

    db.settingUpVersions = true;
    db.error = null;
    db.success = null;

    this.apiService.setupVersions(db.name).subscribe({
      next: (result) => {
        db.success = result.message;
        db.settingUpVersions = false;
        db.statusLoading = true;
        this.apiService.getDatabaseStatus(db.name).subscribe({
          next: (status) => {
            db.isTracked   = status.isTracked;
            db.hasLogs     = status.hasLogs;
            db.hasVersions = status.hasVersions;
            db.hasTrigger  = status.hasTrigger;
            db.statusLoading = false;
          },
          error: () => { db.statusLoading = false; }
        });
      },
      error: (err) => {
        db.error = 'Setup failed: ' + (err.error?.error || err.message);
        db.settingUpVersions = false;
      }
    });
  }
}
